// ─────────────────────────────────────────────
//  native/MobileShellGate.jsx — the ONE isNative() routing branch (mobile redesign S1)
//
//  On WEB this gate returns `children` verbatim — the very element it was
//  given. Not a clone, not a wrapper, no context provider: React's reconciler
//  sees the identical element object, so the web tree is byte-for-byte
//  untouched (guarded by MobileShellGate.test.jsx innerHTML equality).
//
//  On NATIVE (installed Capacitor shell only — NOT a phone browser; see
//  useIsMobile vs isNative in the redesign plan) it lazy-loads MobileShell.
//  React.lazy keeps the entire mobile tree in its own async chunk that a web
//  session never requests, which also closes the ATC module-scope trap: no
//  native-only module can evaluate (and throw) inside the web module graph —
//  the getAnalytics()-on-capacitor:// class of failure.
//
//  This file must stay free of module-scope side effects itself: no Firebase
//  imports, no top-level await, nothing that can throw at module scope.
//  tokens is a plain object; platform.js is environment-agnostic by design.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
// ─────────────────────────────────────────────
import React, { Suspense } from "react";
import tokens from "../theme/tokens";
import { isNative } from "./platform";

const MobileShell = React.lazy(() => import("../pages/mobile/MobileShell"));

// Minimal boot screen shown only for the instant the mobile chunk loads on a
// native cold start. Same tone as the BiometricGate lock screen.
function MobileBootScreen() {
  return (
    <main style={styles.wrap} aria-label="Loading">
      <h1 style={styles.title}>Anesthesia Team Control</h1>
      <p style={styles.sub}>Loading…</p>
    </main>
  );
}

export default function MobileShellGate({ children }) {
  // Web: identical element tree, same references — nothing else may run.
  if (!isNative()) return children;
  // Native: the shell owns the four mobile routes; every other role and route
  // falls through to `fallback` — the untouched web tree passed in as children.
  return (
    <Suspense fallback={<MobileBootScreen />}>
      <MobileShell fallback={children} />
    </Suspense>
  );
}

const { color, space, font } = tokens;

const styles = {
  wrap: {
    position: "fixed",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: space.sm,
    background: color.surface.base,
    padding: space.xl,
    textAlign: "center",
  },
  title: {
    margin: 0,
    color: color.text.primary,
    fontSize: font.size.display,
    fontWeight: font.weight.bold,
  },
  sub: {
    margin: 0,
    color: color.text.secondary,
    fontSize: font.size.body,
  },
};
